import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, errorMessage, get, post } from '../../api.js';
import { Field, Notice } from '../../components/ui.js';
import { RolePicker } from './RolePicker.js';
import type { RoleOption, UserDetail } from './types.js';

/** Creates the account and emails the invitation (choose a password, set up the app). */
export function InviteUser() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roles: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    get<RoleOption[]>('/admin/roles').then(setRoles, (err: unknown) => setError(errorMessage(err)));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (form.roles.length === 0) {
      setError('Choose at least one role.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await post<{ user: UserDetail; invitationSent: boolean }>('/admin/users', form);
      navigate(`/admin/users/${res.user.id}`, {
        replace: true,
        state: {
          notice: res.invitationSent
            ? { kind: 'ok', text: `Invitation sent to ${res.user.email}.` }
            : {
                kind: 'error',
                text: 'The account is created, but the invitation email could not be sent. Try Resend the invitation.',
              },
        },
      });
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'conflict'
          ? 'A user with this email already exists.'
          : err instanceof ApiError && err.code === 'invalid_request'
            ? 'Check the fields: first name, last name, a valid email and at least one role.'
            : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className="page">Invite a user</h1>
      <p className="lede">
        The person receives an email to choose a password, then sets up two-step sign-in with an
        authenticator app.
      </p>
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={(e) => void submit(e)} className="card vstack narrow">
        <div className="g2">
          <Field
            label="First name"
            required
            autoComplete="off"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
          <Field
            label="Last name"
            required
            autoComplete="off"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </div>
        <Field
          label="Email"
          type="email"
          required
          autoComplete="off"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <RolePicker
          roles={roles}
          value={form.roles}
          onChange={(r) => setForm({ ...form, roles: r })}
        />
        <div className="actions">
          <button type="submit" className="btn primary" disabled={busy}>
            Send the invitation
          </button>
          <Link className="btn" to="/admin/users">
            Cancel
          </Link>
        </div>
      </form>
    </>
  );
}
